/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../api.js";
import _ from "underscore";
import googleMaps from '../util/google-maps.js';
import bibyProgram from '../programs/biby.js';
import util from "util/util.js";

import '../util/custom-bindings.js';
import '../util/knockout-pikaday.js';

var SurveyBibySiteViewModel = function(params) {
	var self = this;

  self.survey = params.parent.survey;

  // Machinery to ensure we only initialize when needed, otherwise all surveys will have to init BIBY stuff for no reason
  self.initialized = ko.observable(false);
  ko.computed(function() {
    initialize(self.survey.programData().biby);
  });

  function initialize(biby) {
    if(self.initialized.peek() || !biby) return;
    biby = biby.fields;

    self.showNextTab = params.parent.showNextTab;

    self.map = ko.observable();
    self.mapOptions = ko.observable();

    self.siteTypes = ko.observable([]);
    birdata.lookup("biby_site_type").then(self.siteTypes);

    self.states = ko.observable([]);
    birdata.lookup("state").then(self.states);

    self.sites = ko.observable([]);

    self.site = biby.site;

    self.markerLocation = ko.observable(null);
    self.markerDraggable = ko.observable();
    self.marker = ko.observable();


    var userId = params.parent.loadedSurveyJSON ? params.parent.loadedSurveyJSON.user_id : null;

    bibyProgram.sites(userId).then(function(sites) {
      sites = _.flatten([sites, bibyProgram.newSite()]);

      // Important bit of logic here!
      // Each site has an associated site condition. By default, this is the most recent site condition for that site.
      // But if we are loading an existing survey, it may have a site with a different site condition on it. So we need to
      // switch out the menu option for that site with the existing survey's site.
      var currentSite = self.site();
      if(currentSite) {
        sites = _.map(sites, function(s) {
          return (currentSite.id == s.id) ? currentSite : s;
        });
      }

      // console.log(currentSite);

      self.sites(sites);
      if(sites.length == 1) {
        self.site(sites[0]);
      }
    });

    self.isNewSite = ko.pureComputed(function() {
      return !!(self.site() && !self.site().id);
    });

    self.isExistingSite = ko.pureComputed(function() {
      return !!(self.site() && self.site().id);
    });

    self.showNextButton = ko.pureComputed(function() {
      return self.markerLocation();
    });

    function filterJoin(arr, str) {
      return _.filter(arr, function(x) { return !!x; }).join(str);
    }

    self.addressString = ko.pureComputed(function() {
      var site = self.site();
      if(!site) return "";

      var bibySite = site.site_condition?.site;

      if(bibySite?.address1) {

        return filterJoin([
          bibySite.address1,
          bibySite.address2,
          filterJoin([bibySite.suburb, bibySite.state && bibySite.state.toUpperCase(), bibySite.postcode], " ")
        ], "<br>");

      } else if(site.lat && site.lon) {

        return util.formatDecimalCoordinates(site);

      }

      return "";
    });

    self.geocodeStatus = ko.observable("IDLE");

    self.geocodeMessage = ko.pureComputed(function() {
        switch(self.geocodeStatus()) {
          case "IDLE": return "";
          case "OK": return "Please check the site position shown on the map. If it is incorrect, drag the marker to the correct location.";
          case "LOADING": return "Looking up your address...";
          default: return "We're having trouble looking up that address. Please locate your site using the map and click to specify its position.";
        }
    });

    self.geocodeAddress = ko.pureComputed(function() {
      var site = self.site();
      if(site && !site.id && site.address1() && site.suburb() && site.state()) {
        return filterJoin([
          site.address1(),
          site.address2(),
          filterJoin([site.suburb(), site.state().name, site.postcode()], " ")], ", ");
      }
    });

    self.showMap = ko.pureComputed(function() {
      return self.site() && (self.site().id || !!self.geocodeAddress());
    });

    // Update map & marker when site changes
    ko.computed(function() {
      var site = self.site();
      var map = self.map();
      if(map) {
        setTimeout(function() {
          if(site && site.id) {
            var location = new google.maps.LatLng(site.lat, site.lon);
            self.markerLocation(location);
            self.markerDraggable(false);
            map.setCenter(location);
            map.setZoom(16);
          } else {
            self.markerLocation(null);
            self.markerDraggable(true);
          }
        });
      }
    });

    // Launch geocoding when address changes
    ko.computed(function() {
      var address = self.geocodeAddress();
      if(address) {
        self.geocodeStatus("LOADING");

        var geocoder = new google.maps.Geocoder(); // assuming google maps is loaded
        geocoder.geocode({ address: address, region: 'au' }, function(results, status) {
          self.geocodeStatus(status);
          if(status == 'OK') {
            var location = results[0].geometry.location;
            var map = self.map();
            if(map) {
              google.maps.event.trigger(map, 'resize');
              self.markerLocation(location);
              map.fitBounds(results[0].geometry.viewport);
            }
          }
        });
      }
    });

    // Resize & Recenter map after changing tab
    ko.computed(function() {
      var map = self.map();
      if(map && params.parent.currentSection().id == 'biby_site') {
        google.maps.event.trigger(map, 'resize');
        if(self.markerLocation()) {
          map.setCenter(self.markerLocation());
        }
      }
    });

    // Update marker based on observables
    ko.computed(function() {
      var marker = self.marker();
      var location = self.markerLocation();
      var draggable = self.markerDraggable();

      if(marker) {
        marker.setOptions({
          map: location ? self.map() : null,
          position: location || marker.getPosition(),
          draggable: draggable
        });
      }
    });

    // Clicking on map adds marker if not set yet
    self.map.subscribe(function(map) {
      if(map) {
        google.maps.event.addListener(map, 'click', function(evt) {
          if(!self.markerLocation()) {
            self.markerLocation(evt.latLng);
          }
        });
      }
    });

    // Update survey point
    ko.computed(function() {
      var site = self.site();
      if(site) {
        if(site.id) {
          self.survey.surveyPoint({
            id: site.id,
            lat: site.lat,
            lon: site.lon,
            sharedSites: ko.observable([]),
            name: site.name,
            accuracy: ko.observable(null),
            survey_count: site.survey_count
          });
        } else if(self.markerLocation()) {
          self.survey.surveyPoint({
            lat: self.markerLocation().lat(),
            lon: self.markerLocation().lng(),
            name: site.address1,
            sharedSites: ko.observable([]),
            accuracy: ko.observable(null)
          });
        }
      } else {
        self.survey.surveyPoint(null);
      }
    });

    // setup map
    googleMaps.load().then(function() {
      var center = new google.maps.LatLng(-22.50831, 136.078612);
      var zoom = 4;

      self.mapOptions(googleMaps.defaultOptions({
        center: center,
        zoom: zoom,
        fullscreenControl: false
      }));

      self.marker(new google.maps.Marker({
        position: google.maps.LatLng(0)
      }));

      google.maps.event.addListener(self.marker(), 'dragend', function() {
        self.markerLocation(self.marker().getPosition());
      });
    });

    self.initialized(true);
  }
};

ko.components.register('survey-edit-biby-site', {
	viewModel: SurveyBibySiteViewModel,
	template: require('../../templates/survey-edit-biby-site.html')
});

export default SurveyBibySiteViewModel;
